/**
 * Reusable helpers to add a free-text "include <searchText>" option to any
 * async searchable autocomplete field (react-select `loadOptions` + `getOptionLabel`).
 *
 * Usage for any field:
 *   const config = { accessKey: 'c_make' };
 *   loadOptions: withIncludeOption(originalLoadOptions, config)
 *   getOptionLabel: withIncludeOptionLabel(originalGetOptionLabel, config)
 */

export const INCLUDE_OPTION_FLAG = '__isIncludeOption';

export type IncludeOptionConfig = {
  accessKey: string;
  prefix?: string;
};

export const isIncludeOption = (option: any): boolean =>
  !!(option && (option[INCLUDE_OPTION_FLAG] || option.isInclude));

export const buildIncludeOption = (searchText: string, accessKey: string) => ({
  [accessKey]: searchText,
  [INCLUDE_OPTION_FLAG]: true,
});

export const withIncludeOptionLabel =
  (getOptionLabel: (option: any) => string, { accessKey, prefix = 'Includes' }: IncludeOptionConfig) =>
  (option: any) => {
    if (isIncludeOption(option)) {
      return `${prefix} "${option[accessKey] || option.value || ''}"`;
    }
    return getOptionLabel(option);
  };

export const withIncludeOption =
  (
    loadOptions: (value: string, cb: (options: any[]) => void) => void,
    { accessKey }: IncludeOptionConfig,
  ) =>
  (value: string, cb: (options: any[]) => void) => {
    loadOptions(value, (options: any[] = []) => {
      const trimmed = (value || '').trim();
      if (!trimmed) {
        cb(options);
        return;
      }
      cb([buildIncludeOption(trimmed, accessKey), ...options]);
    });
  };
